import { useCallback, useEffect, useState } from 'react';
import { useThemeStore } from '@/stores/theme';
import { Toaster } from 'sonner';
import { useAuthStore } from '@/stores/auth';
import { useBrandingStore } from '@/stores/branding';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { LoginPage } from '@/pages/auth/LoginPage';
import { RegisterPage } from '@/pages/auth/RegisterPage';
import { SSOCallbackPage } from '@/pages/auth/SSOCallbackPage';
import { DashboardPage } from '@/pages/dashboard/DashboardPage';
import { ProfilePage } from '@/pages/profile/ProfilePage';
import { MarketingPlanPage } from '@/pages/marketing-plan/MarketingPlanPage';
import { ActivitiesPage } from '@/pages/activities/ActivitiesPage';
import { ActivityDetailPage } from '@/pages/activities/ActivityDetailPage';
import { CampaignsPage } from '@/pages/campaigns/CampaignsPage';
import { CampaignDetailPage } from '@/pages/campaigns/CampaignDetailPage';
import { StatisticsPage } from '@/pages/statistics/StatisticsPage';
import { TeamsPage } from '@/pages/teams/TeamsPage';
import { SuppliersPage } from '@/pages/suppliers/SuppliersPage';
import { SupplierDetailPage } from '@/pages/suppliers/SupplierDetailPage';
import { HelpPage } from '@/pages/help/HelpPage';
import { UsersAdminPage } from '@/pages/admin/UsersAdminPage';
import { RolesAdminPage } from '@/pages/admin/RolesAdminPage';
import { IntegrationsAdminPage } from '@/pages/admin/IntegrationsAdminPage';
import { SettingsAdminPage } from '@/pages/admin/SettingsAdminPage';
import { NotificationsAdminPage } from '@/pages/admin/NotificationsAdminPage';
import { AuditLogAdminPage } from '@/pages/admin/AuditLogAdminPage';
import { DocumentsPage } from '@/pages/documents/DocumentsPage';
import { AIAssistantsPage } from '@/pages/ai/AIAssistantsPage';
import { OnboardingPage } from '@/pages/onboarding/OnboardingPage';
import { NotificationsPage } from '@/pages/notifications/NotificationsPage';
import { AutomationPage } from '@/pages/automation/AutomationPage';
import { OnboardingChecklist } from '@/components/onboarding/OnboardingChecklist';
import { ConfirmDialogProvider } from '@/components/ui/confirm-dialog';
import { AnalyticsConsentBanner } from '@/components/common/AnalyticsConsentBanner';
import { initAnalytics, trackPageView } from '@/lib/analytics';
import { MarketingStrategyPage } from '@/pages/marketing-strategy/MarketingStrategyPage';
import { StrategyPanel } from '@/components/strategy/StrategyPanel';
import { SupplierPortalPage } from '@/pages/supplier-portal/SupplierPortalPage';
import { isSupplierPortalUser } from '@/lib/supplier-portal-user';
import { getReturnPathFromHash } from '@/lib/return-path';
import { decodeHashRouteParam } from '@/lib/hash-route';
import { Button } from '@/components/ui/button';
import { ComposerPage } from '@/pages/composer/ComposerPage';
import { ApprovePage } from '@/pages/approvals/ApprovePage';
import { SocialHubPage } from '@/pages/social/SocialHubPage';
import { ContentCalendarPage } from '@/pages/calendar/ContentCalendarPage';
import { ContentPlannerPage } from '@/pages/planner/ContentPlannerPage';
import { MediaLibraryPage } from '@/pages/media/MediaLibraryPage';

function getHashRoute(): string {
  const hash = window.location.hash.slice(1) || '/';
  const hashPath = hash.split('?')[0] || '/';
  const path = hashPath.startsWith('/') ? hashPath : `/${hashPath}`;
  return path === '/' ? '/dashboard' : path;
}

/** Legacy path redirects to the Command Center IA. */
function resolveCanonicalPath(path: string): string {
  if (path === '/linkedin') return '/social';
  if (path === '/marketing-calendar') return '/calendar';
  if (path === '/activities/calendar') return '/calendar';
  return path;
}

function isAuthHashPath(): boolean {
  const path = getHashRoute();
  return path === '/auth/login' || path === '/auth/register' || path === '/auth/callback';
}

/** Auth screens use a light card on a bright photo — keep document light so inputs/buttons match. */
function ThemeRoot() {
  const theme = useThemeStore((s) => s.theme);
  useEffect(() => {
    const apply = () => {
      if (isAuthHashPath()) {
        document.documentElement.classList.remove('dark');
        return;
      }
      const isDark =
        theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
      document.documentElement.classList.toggle('dark', isDark);
    };
    apply();
    window.addEventListener('hashchange', apply);
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onSystem = () => {
      if (theme === 'system') apply();
    };
    mq.addEventListener('change', onSystem);
    return () => {
      window.removeEventListener('hashchange', apply);
      mq.removeEventListener('change', onSystem);
    };
  }, [theme]);
  return null;
}

function AuthProvider({ children }: { children: React.ReactNode }) {
  const loadUser = useAuthStore((s) => s.loadUser);

  useEffect(() => {
    loadUser();
    void useBrandingStore.getState().load();
  }, [loadUser]);

  return <>{children}</>;
}

function ProtectedRoute({
  children,
  isAuthenticated,
  onNavigate,
}: {
  children: React.ReactNode;
  isAuthenticated: boolean;
  onNavigate: (path: string) => void;
}) {
  useEffect(() => {
    if (!isAuthenticated) {
      onNavigate('/auth/login');
    }
  }, [isAuthenticated, onNavigate]);

  if (!isAuthenticated) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-border border-t-primary-600 dark:border-t-primary-400" />
      </div>
    );
  }

  return <>{children}</>;
}

function AdminRoute({
  children,
  roles,
  onNavigate,
}: {
  children: React.ReactNode;
  roles: string[];
  onNavigate: (path: string) => void;
}) {
  const isAdmin = roles.includes('super_admin') || roles.includes('admin');

  useEffect(() => {
    if (!isAdmin) {
      onNavigate('/dashboard');
    }
  }, [isAdmin, onNavigate]);

  if (!isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <h1 className="text-xl font-semibold text-foreground">Kein Zugriff</h1>
        <p className="mt-2 text-muted">Diese Seite ist nur für Administratoren verfügbar.</p>
      </div>
    );
  }

  return <>{children}</>;
}

function NotFoundPage({ onNavigate }: { onNavigate: (path: string) => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-16">
      <h1 className="text-2xl font-semibold text-foreground">Seite nicht gefunden</h1>
      <p className="mt-2 text-muted">Der Link ist ungültig oder nicht mehr verfügbar.</p>
      <Button className="mt-4" onClick={() => onNavigate('/dashboard')}>Zur Übersicht</Button>
    </div>
  );
}

function AppContent() {
  const [route, setRoute] = useState<string>(getHashRoute());
  const [navHistory, setNavHistory] = useState<string[]>([]);
  const isLoading = useAuthStore((s) => s.isLoading);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const user = useAuthStore((s) => s.user);
  const roleList = (user as { roles?: string[] } | null)?.roles ?? [];
  const supplierPortalUser = isSupplierPortalUser(roleList);

  useEffect(() => {
    const handler = () => {
      const next = getHashRoute();
      const canonical = resolveCanonicalPath(next);
      if (canonical !== next) {
        const params = window.location.hash.includes('?')
          ? window.location.hash.slice(window.location.hash.indexOf('?'))
          : next === '/linkedin'
            ? '?platform=linkedin'
            : next === '/activities/calendar'
              ? '?layer=activities'
              : '';
        window.location.hash = `${canonical}${params}`;
        setRoute(canonical);
        return;
      }
      setRoute(next);
    };
    handler();
    window.addEventListener('hashchange', handler);
    return () => window.removeEventListener('hashchange', handler);
  }, []);

  useEffect(() => {
    initAnalytics();
    trackPageView(route);
  }, [route]);

  const onNavigate = useCallback((path: string) => {
    const raw = window.location.hash.slice(1) || '/';
    const historyEntry = raw.startsWith('/') ? raw : `/${raw}`;
    setNavHistory((prev) => [...prev, historyEntry]);
    const normalized = path.startsWith('/') ? path : `/${path}`;
    window.location.hash = normalized;
    setRoute(resolveCanonicalPath(getHashRoute()));
  }, []);

  const onBack = (fallback: string = '/dashboard') => {
    if (navHistory.length > 0) {
      const prev = navHistory.at(-1);
      if (prev == null) {
        setNavHistory([]);
        window.location.hash = fallback.startsWith('/') ? fallback : `/${fallback}`;
        setRoute(getHashRoute());
        return;
      }
      setNavHistory((h) => h.slice(0, -1));
      window.location.hash = prev.startsWith('/') ? prev : `/${prev}`;
      setRoute(getHashRoute());
    } else {
      setNavHistory([]);
      window.location.hash = fallback.startsWith('/') ? fallback : `/${fallback}`;
      setRoute(getHashRoute());
    }
  };

  const currentPath = route;
  const campaignDetailMatch = currentPath.match(/^\/campaigns\/([^/]+)$/);
  const campaignDetailId = decodeHashRouteParam(campaignDetailMatch?.[1]);
  const supplierDetailMatch = currentPath.match(/^\/suppliers\/([^/]+)$/);
  const supplierDetailId = decodeHashRouteParam(supplierDetailMatch?.[1]);
  const activityDetailMatch = currentPath.match(/^\/activities\/([^/]+)$/);
  const activityDetailId = decodeHashRouteParam(activityDetailMatch?.[1]);

  const returnFromQuery = getReturnPathFromHash();
  const activityBackFallback = returnFromQuery ?? '/activities';
  const campaignBackFallback = returnFromQuery ?? '/campaigns';
  const supplierBackFallback = returnFromQuery ?? '/suppliers';
  const composerMatch = currentPath.match(/^\/composer\/([^/]+)$/);
  const composerPostId = decodeHashRouteParam(composerMatch?.[1]);
  const approveMatch = currentPath.match(/^\/approve\/([^/]+)$/);
  const approveToken = decodeHashRouteParam(approveMatch?.[1]);
  const socialAccountsPath = currentPath === '/social/accounts';
  const socialQueuePath = currentPath === '/social/queue';
  const knownRoutes = [
    '/dashboard',
    '/profile',
    '/marketing-plan',
    '/marketing-strategy',
    '/activities',
    '/activities/calendar',
    '/marketing-calendar',
    '/calendar',
    '/campaigns',
    '/statistics',
    '/teams',
    '/suppliers',
    '/supplier-portal',
    '/documents',
    '/onboarding',
    '/notifications',
    '/automation',
    '/ai',
    '/companion',
    '/composer',
    '/planner',
    '/social',
    '/social/accounts',
    '/social/queue',
    '/media',
    '/help',
    '/admin/users',
    '/admin/roles',
    '/admin/integrations',
    '/admin/settings',
    '/admin/notifications',
    '/admin/audit',
    '/linkedin',
  ];

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-border border-t-primary-600 dark:border-t-primary-400" />
      </div>
    );
  }

  const isAuthRoute =
    currentPath === '/auth/login' ||
    currentPath === '/auth/register' ||
    currentPath === '/auth/callback';

  if (isAuthRoute) {
    return (
      <>
        <Toaster richColors position="top-right" />
        {currentPath === '/auth/login' && <LoginPage onNavigate={onNavigate} />}
        {currentPath === '/auth/register' && <RegisterPage onNavigate={onNavigate} />}
        {currentPath === '/auth/callback' && <SSOCallbackPage onNavigate={onNavigate} />}
        <AnalyticsConsentBanner />
      </>
    );
  }

  if (approveToken) {
    return (
      <>
        <Toaster richColors position="top-right" />
        <ApprovePage token={approveToken} />
        <AnalyticsConsentBanner />
      </>
    );
  }

  return (
    <>
      <Toaster richColors position="top-right" />
      <ProtectedRoute isAuthenticated={isAuthenticated} onNavigate={onNavigate}>
        <DashboardLayout currentPath={currentPath} onNavigate={onNavigate}>
          {supplierPortalUser && (currentPath === '/dashboard' || currentPath === '/supplier-portal') && (
            <SupplierPortalPage onNavigate={onNavigate} />
          )}
          {currentPath === '/dashboard' && !supplierPortalUser && <DashboardPage onNavigate={onNavigate} />}
          {currentPath === '/supplier-portal' && !supplierPortalUser && <DashboardPage onNavigate={onNavigate} />}
          {currentPath === '/profile' && <ProfilePage />}
          {currentPath === '/marketing-plan' && <MarketingPlanPage />}
          {currentPath === '/marketing-strategy' && <MarketingStrategyPage />}
          {currentPath === '/activities' && (
            <ActivitiesPage
              defaultView="kanban"
              onNavigate={onNavigate}
              listPath={currentPath}
            />
          )}
          {currentPath === '/calendar' && <ContentCalendarPage onNavigate={onNavigate} />}
          {currentPath === '/planner' && <ContentPlannerPage onNavigate={onNavigate} />}
          {currentPath === '/composer' && <ComposerPage onNavigate={onNavigate} />}
          {composerPostId && <ComposerPage postId={composerPostId} onNavigate={onNavigate} />}
          {currentPath === '/social' && <SocialHubPage onNavigate={onNavigate} />}
          {socialAccountsPath && <SocialHubPage onNavigate={onNavigate} initialTab="accounts" />}
          {socialQueuePath && <SocialHubPage onNavigate={onNavigate} initialTab="queue" />}
          {currentPath === '/media' && <MediaLibraryPage onNavigate={onNavigate} />}
          {currentPath === '/campaigns' && <CampaignsPage />}
          {campaignDetailId && (
            <CampaignDetailPage
              campaignId={campaignDetailId}
              onNavigate={onNavigate}
              onBack={() => onBack(campaignBackFallback)}
            />
          )}
          {currentPath === '/statistics' && <StatisticsPage onNavigate={onNavigate} />}
          {currentPath === '/teams' && <TeamsPage />}
          {currentPath === '/suppliers' && <SuppliersPage onNavigate={onNavigate} />}
          {supplierDetailId && (
            <SupplierDetailPage
              supplierId={supplierDetailId}
              onNavigate={onNavigate}
              onBack={() => onBack(supplierBackFallback)}
            />
          )}
          {activityDetailId && (
            <ActivityDetailPage
              activityId={activityDetailId}
              onNavigate={onNavigate}
              onBack={() => onBack(activityBackFallback)}
            />
          )}
          {currentPath === '/documents' && <DocumentsPage />}
          {currentPath === '/onboarding' && <OnboardingPage />}
          {currentPath === '/notifications' && <NotificationsPage onNavigate={onNavigate} />}
          {currentPath === '/automation' && <AutomationPage />}
          {currentPath === '/ai' && <AIAssistantsPage />}
          {currentPath === '/companion' && <AIAssistantsPage />}
          {currentPath === '/help' && <HelpPage />}
          {currentPath === '/admin/users' && (
            <AdminRoute roles={roleList} onNavigate={onNavigate}>
              <UsersAdminPage />
            </AdminRoute>
          )}
          {currentPath === '/admin/roles' && (
            <AdminRoute roles={roleList} onNavigate={onNavigate}>
              <RolesAdminPage />
            </AdminRoute>
          )}
          {currentPath === '/admin/integrations' && (
            <AdminRoute roles={roleList} onNavigate={onNavigate}>
              <IntegrationsAdminPage />
            </AdminRoute>
          )}
          {currentPath === '/admin/settings' && (
            <AdminRoute roles={roleList} onNavigate={onNavigate}>
              <SettingsAdminPage />
            </AdminRoute>
          )}
          {currentPath === '/admin/notifications' && (
            <AdminRoute roles={roleList} onNavigate={onNavigate}>
              <NotificationsAdminPage />
            </AdminRoute>
          )}
          {currentPath === '/admin/audit' && (
            <AdminRoute roles={roleList} onNavigate={onNavigate}>
              <AuditLogAdminPage />
            </AdminRoute>
          )}
          {!knownRoutes.includes(currentPath) && !campaignDetailId && !supplierDetailId && !activityDetailId && !composerPostId && !approveToken && (
            <NotFoundPage onNavigate={onNavigate} />
          )}
        </DashboardLayout>
        {!supplierPortalUser && <OnboardingChecklist />}
        {!supplierPortalUser && <StrategyPanel onNavigate={onNavigate} />}
      </ProtectedRoute>
      <AnalyticsConsentBanner />
    </>
  );
}

export default function App() {
  return (
    <ConfirmDialogProvider>
      <AuthProvider>
        <ThemeRoot />
        <AppContent />
      </AuthProvider>
    </ConfirmDialogProvider>
  );
}
